import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import {
  Card,
  CardContent,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@wondermaps/ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuilding } from "@fortawesome/pro-solid-svg-icons/faBuilding";
import { faUsers } from "@fortawesome/pro-solid-svg-icons/faUsers";
import { TierBadge } from "../components/superadmin/TierBadge";

export const Route = createFileRoute("/superadmin/orgs/")({
  component: SuperadminOrgsList,
});

type Organization = {
  id: string;
  name: string;
  slug: string;
  type: string | null;
  tier: string;
  memberCount: number;
  createdAt: string;
};

function SuperadminOrgsList() {
  const navigate = useNavigate();

  // Fetch organizations
  const { data, isLoading } = useQuery({
    queryKey: ["superadmin", "orgs"],
    queryFn: async () => {
      const res = await api.orgs.admin.$get();
      if (!res.ok) throw new Error("Failed to fetch organizations");
      return res.json() as Promise<{ orgs: Organization[] }>;
    },
  });

  const orgs = data?.orgs ?? [];

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Tier</TableHead>
                <TableHead className="text-right">Members</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[1, 2, 3].map((i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton className="h-5 w-32" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                  <TableCell className="text-right"><Skeleton className="h-5 w-8 ml-auto" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  }

  if (orgs.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <FontAwesomeIcon icon={faBuilding} className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No organizations yet.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Tier</TableHead>
              <TableHead className="text-right">Members</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orgs.map((org) => (
              <TableRow
                key={org.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => navigate({ to: "/superadmin/orgs/$orgId", params: { orgId: org.id } })}
              >
                <TableCell className="font-medium">{org.name}</TableCell>
                <TableCell className="text-muted-foreground">{org.slug}</TableCell>
                <TableCell className="capitalize">{org.type ?? "default"}</TableCell>
                <TableCell><TierBadge tier={org.tier ?? "base"} /></TableCell>
                <TableCell className="text-right">
                  <span className="inline-flex items-center gap-1">
                    <FontAwesomeIcon icon={faUsers} className="h-3 w-3" />
                    {org.memberCount}
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

